import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { IciFlags } from 'src/generated/signalR/ici-flags';

import { SortDescriptor } from '@progress/kendo-data-query';
import { MessageEntryRemoteDto } from 'src/generated/signalR/message-entry-remote-dto';
import { SamwinMessageInfoRemoteDto } from 'src/generated/signalR/samwin-message-info-remote-dto';
import { SessionState } from '../../session/session.state';
import { CallTagsState } from '../call-tags/call-tags.state';
import { EmailBehaviourState } from '../email/email-behaviour/email-behaviour.state';
import { SamwinAgentListState } from '../samwin-agent-list/samwin-agent-list.state';
import { SamwinMessageLinesState } from '../samwin-message-lines/samwin-message-lines.state';
import { MailingFolderEnum } from '../samwin-skills/mailing-folders-enum';
import { SamwinSkillsState } from '../samwin-skills/samwin-skills.state';
import { SkillConstants } from '../samwin-skills/skill-constants';
import { SkillGroup } from '../samwin-skills/skill-group';
import { MessageInfo, MessageStatus } from './message-info';
import { MessageListEntrySelected, MessageListSelectNextEntry } from './samwin-message-list.actions';
import { SamwinMessageListState, SamwinMessageListStateModel } from './samwin-message-list.state';

// tslint:disable-next-line:no-empty-interface
export interface SamwinMessageListStateSelectorsModel {}

@State<SamwinMessageListStateSelectorsModel>({
   name: 'samwinMessageListStateSelectors',
   defaults: {},
})
@Injectable()
export class SamwinMessageListStateSelectors {
   constructor(private store: Store) {}
   @Selector([
      SamwinMessageListState.waitList,
      SamwinSkillsState.getSkillGroupById,
      SamwinAgentListState.getAgentDetailsForUserId,
      CallTagsState.getAreMessageTagsAvailable,
      CallTagsState.getMessageTag,
      SamwinMessageLinesState.isLineAssigned,
      EmailBehaviourState.isEmailCreated,
      EmailBehaviourState.isEmailOpened,
      EmailBehaviourState.isOpen,
   ])
   public static getWaitlist(
      waitList: SamwinMessageInfoRemoteDto[],
      getSkillById: (skillId: number) => SkillGroup,
      getAgentByUserId: (userId: number) => AgentListEntryRemoteDto,
      getAreMessageTagsAvailable: (messageId: string) => boolean,
      getMessageTag: (callId: number, messageId: string, tagName: string) => string,
      isLineAssigned: (callId: number) => boolean,
      isEmailCreated: (messageId: string) => boolean,
      isEmailOpened: (messageId: string) => boolean
   ) {
      const memoTag = 'MEMO';
      return waitList.map(
         (item) =>
            new MessageInfo(
               item.messageId,
               item.callId,
               MailingFolderEnum.INBOX.name,
               getSkillById(item.queueId),
               item.peerId,
               item.subject,
               getAreMessageTagsAvailable(item.messageId) ? getMessageTag(item.callId, item.messageId, memoTag) : item.tags[memoTag],
               getAgentByUserId(item.assignedUserId)?.displayName ?? '',
               item.timestamp,
               new MessageStatus(isEmailCreated(item.messageId), isLineAssigned(item.callId), isEmailOpened(item.messageId)),
               item.queueId,
               item.skillValue,
               item.ici.ici & IciFlags.PARK_PRIVATE ? true : false,
               item.ici.ici & IciFlags.PARK_PUBLIC ? true : false
            )
      );
   }

   @Selector([
      SamwinMessageListState.messages,
      SamwinMessageListState.customSorting,
      SamwinSkillsState.getSkillGroupById,
      SamwinAgentListState.getAgentDetailsForUserId,
      EmailBehaviourState.isEmailCreated,
      EmailBehaviourState.isEmailOpened,
      EmailBehaviourState.isOpen,
   ])
   public static getMessages(
      messages: {
         [messageId: string]: MessageEntryRemoteDto;
      },
      customSorting: SortDescriptor[],
      getSkillById: (skillId: number) => SkillGroup,
      getAgentByUserId: (userId: number) => AgentListEntryRemoteDto,
      isEmailCreated: (messageId: string) => boolean,
      isEmailOpened: (messageId: string) => boolean
   ) {
      const msgs = _(messages)
         .map((item) => {
            const groupName: string = _(MailingFolderEnum.values())
               .filter((mailEnum) => mailEnum.value === item.wellKnownFolder)
               .head().name;
            const isIncoming: boolean = !(groupName === MailingFolderEnum.SENT.name) && !(groupName === MailingFolderEnum.OUTBOX.name);
            return new MessageInfo(
               item.messageId,
               item.callId,
               groupName,
               getSkillById(item.queueId),
               isIncoming ? item.peerId : item.targetPeerId,
               item.subject,
               item.memo,
               getAgentByUserId(item.userId)?.displayName ?? '',
               item.sentTimestamp,
               new MessageStatus(isEmailCreated(item.messageId), false, isEmailOpened(item.messageId))
            );
         })
         .value();
      return SamwinMessageListState.sortMessages(msgs, customSorting);
   }

   @Selector([
      SamwinMessageListState.filteredMessages,
      SamwinMessageListState.customSorting,
      SessionState.getAuthenticatedUserId,
      SamwinSkillsState.getSkillGroupById,
      SamwinSkillsState.isUserLoggedOnToSkill,
      SamwinAgentListState.getAgentDetailsForUserId,
      SamwinMessageListState.getSkillProgressionFromWaitlist,
      SamwinMessageListStateSelectors.getCallIdByMessageId,
      EmailBehaviourState.isEmailCreated,
      EmailBehaviourState.isEmailOpened,
      SamwinMessageListStateSelectors.isParkedByAgent,
      SamwinMessageListStateSelectors.isParkedBySkill,
   ])
   public static getSearchedMessages(
      filteredMessages: MessageEntryRemoteDto[],
      customSorting: SortDescriptor[],
      userId: number,
      getSkillById: (skillId: number) => SkillGroup,
      isUserLoggedOnToSkill: (skillId: number, userId: number) => boolean,
      getAgentbyUserId: (userId: number) => AgentListEntryRemoteDto,
      getSkillProgressionFromWaitlist: (messageId: string) => number,
      getCallId: (messageId: string) => number,
      isEmailCreated: (messageId: string) => boolean,
      isEmailOpened: (messageId: string) => boolean,
      isParkedByAgent: (messageId: string) => boolean,
      isParkedBySkill: (messageId: string) => boolean
   ) {
      const messages = filteredMessages
         .map((item) => {
            const groupName: string = _(MailingFolderEnum.values())
               .filter((mailEnum) => mailEnum.value === item.wellKnownFolder)
               .head().name;
            const isInbox = groupName === MailingFolderEnum.INBOX.name;
            const isIncoming: boolean = !(groupName === MailingFolderEnum.SENT.name) && !(groupName === MailingFolderEnum.OUTBOX.name);
            const skillProgressionvalue: number = isInbox ? getSkillProgressionFromWaitlist(item.messageId) : 100;

            return new MessageInfo(
               item.messageId,
               getCallId(item.messageId) ?? 0,
               groupName,
               getSkillById(item.queueId),
               isIncoming ? item.peerId : item.targetPeerId,
               item.subject,
               item.memo,
               getAgentbyUserId(item.userId)?.displayName ?? '',
               item.sentTimestamp,
               new MessageStatus(isEmailCreated(item.messageId), false, isEmailOpened(item.messageId)),
               item.queueId,
               skillProgressionvalue,
               isParkedByAgent(item.messageId) ?? null,
               isParkedBySkill(item.messageId ?? null)
            );
         })
         .filter((message) => !_.isNil(message));
      const inboxMessages = messages.filter((message) => message.group === MailingFolderEnum.INBOX.name && isUserLoggedOnToSkill(message.queueId, userId));
      const otherMessages = messages.filter((message) => message.group !== MailingFolderEnum.INBOX.name);
      return _.concat(inboxMessages, SamwinMessageListState.sortMessages(otherMessages, customSorting));
   }

   @Selector([SamwinMessageListState.waitList])
   public static isParkedByAgent(waitList: SamwinMessageInfoRemoteDto[]): (messageID: string) => boolean {
      return (messageID: string) => {
         const result = _(waitList)
            .filter((message) => message.messageId === messageID)
            .head();

         if (!_.isNil(result) && result.ici.ici & IciFlags.PARK_PRIVATE) {
            return true;
         }

         return false;
      };
   }

   @Selector([SamwinMessageListState.waitList])
   public static isParkedBySkill(waitList: SamwinMessageInfoRemoteDto[]): (messageID: string) => boolean {
      return (messageID: string) => {
         const result = _(waitList)
            .filter((message) => message.messageId === messageID)
            .head();

         if (!_.isNil(result) && result.ici.ici & IciFlags.PARK_PUBLIC) {
            return true;
         }

         return false;
      };
   }

   @Selector([SamwinMessageListState.getWaitListKeys])
   public static getMessageIdByCallId(keys: { [Callid: number]: string }) {
      return (callId: number) => {
         return keys[callId];
      };
   }

   @Selector([SamwinMessageListStateSelectors.getWaitlist])
   public static getCallIdByMessageId(messages: MessageInfo[]) {
      return (messageId: string) => {
         const result = _(messages)
            .filter((message) => message.messageId === messageId)
            .head();
         return result?.callId;
      };
   }

   @Selector([SamwinMessageListStateSelectors.getWaitlist, SamwinMessageListStateSelectors.getMessages, SamwinMessageListStateSelectors.getSearchedMessages])
   public static getAllLoadedMessages(waitlist: MessageInfo[], messages: MessageInfo[], searchedMessages: MessageInfo[]): MessageInfo[] {
      return _.concat(waitlist, messages, searchedMessages);
   }

   @Selector([SamwinMessageListStateSelectors.getWaitlist, SamwinMessageListStateSelectors.getMessages])
   public static getCurrentlyLoadedMessages(waitlist: MessageInfo[], messages: MessageInfo[]): MessageInfo[] {
      return _.concat(waitlist, messages);
   }

   @Selector([
      SamwinMessageListState.getSearchValue,
      SamwinMessageListStateSelectors.getCurrentlyLoadedMessages,
      SamwinMessageListStateSelectors.getSearchedMessages,
   ])
   public static getMessageList(searchValue: string, getCurrentlyLoadedMessages: MessageInfo[], searchedMessages: MessageInfo[]): MessageInfo[] {
      if (!_.isEmpty(searchValue)) {
         return searchedMessages;
      }
      return getCurrentlyLoadedMessages;
   }

   @Selector([SamwinMessageListStateSelectors.getAllLoadedMessages])
   public static getMessageById(messages: MessageInfo[]) {
      return (messageId: string) => {
         const result = _(messages)
            .filter((message) => message.messageId === messageId)
            .head();
         return result;
      };
   }

   @Selector([SamwinMessageListStateSelectors.getAllLoadedMessages])
   public static getMessageFolderById(messages: MessageInfo[]) {
      return (messageId: string) => {
         const result = _(messages)
            .filter((message) => message.messageId === messageId)
            .head();
         return result?.group;
      };
   }

   @Selector([SamwinMessageListStateSelectors.getMessageList, SamwinMessageListState.getGroupFilter, SamwinMessageListState.getFilteredSkillId])
   public static getFilteredMessageList(messages: MessageInfo[], group: string, skillId: number): MessageInfo[] {
      const result = messages.filter(
         (message) =>
            !_.isNil(group) &&
            !_.isNil(message.skill) &&
            group === message.group &&
            (skillId === message.skill.skillId || skillId === SkillConstants.OVERALL_SKILL_ID)
      );
      return result;
   }

   @Selector([SamwinMessageListState.selectedRowID, SamwinMessageListStateSelectors.getFilteredMessageList])
   public static getSelectedRow(selectedRowID: string, messages: MessageInfo[]) {
      if (_.isNil(selectedRowID) || _.isNil(messages)) {
         return;
      }
      return messages.find((value) => value.messageId === selectedRowID);
   }

   @Selector([SamwinMessageListState.selectedRowID, SamwinMessageListStateSelectors.getFilteredMessageList])
   public static getSelectedRowIndex(selectedRowID: string, messages: MessageInfo[]) {
      if (_.isNil(selectedRowID) || _.isNil(messages)) {
         return;
      }
      return messages.findIndex((value) => value.messageId === selectedRowID);
   }

   @Selector([SamwinMessageListStateSelectors.getCurrentlyLoadedMessages])
   public static getMessageCount(messages: MessageInfo[]) {
      return (folder: string, skillId: number) => {
         return messages.filter((message) => message.group === folder && (_.isNil(skillId) || message?.skill?.skillId === skillId)).length;
      };
   }

   @Action(MessageListSelectNextEntry)
   public selectNextEntry(ctx: StateContext<SamwinMessageListStateModel>, action: MessageListSelectNextEntry) {
      const selectedRow = this.store.selectSnapshot(SamwinMessageListState.selectedRowID);

      const currentValues = this.store.selectSnapshot(SamwinMessageListStateSelectors.getFilteredMessageList);

      let currentIndex = _(currentValues).findIndex((value) => value.messageId === selectedRow);
      if (_.isNil(currentIndex)) {
         currentIndex = -1;
      }
      const lastIndex = _(currentValues).findLastIndex();
      let nextIndex = currentIndex + (action.direction === 'up' ? -action.step : action.step);
      if (nextIndex > lastIndex) {
         nextIndex = lastIndex;
      }
      if (nextIndex < 0) {
         nextIndex = 0;
      }
      const selectedDirectory = currentValues[nextIndex]?.messageId;
      if (_.isNil(selectedDirectory)) {
         return;
      }
      return ctx.dispatch(new MessageListEntrySelected(selectedDirectory));
   }
}
